/**
 * Shared formatters for recharts LabelList display and currency rendering.
 */

export const SYMBOLS = { GBP: '\u00A3', EUR: '\u20AC', USD: '$', DKK: 'kr' };

export function formatCompact(value, currency = 'GBP') {
  if (value === null || value === undefined || isNaN(value)) return '';
  const abs = Math.abs(value);
  const sym = SYMBOLS[currency] || currency + ' ';
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}${sym}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${sign}${sym}${Math.round(abs / 1000)}k`;
  if (abs >= 1_000) return `${sign}${sym}${(abs / 1000).toFixed(1)}k`;
  return `${sign}${sym}${Math.round(abs)}`;
}

/** Full-precision currency: '£1,234.56' or '€1,234.56' */
export function formatCurrencyFull(value, currency = 'GBP') {
  if (value === null || value === undefined || isNaN(value)) return '';
  const sym = SYMBOLS[currency] || currency + ' ';
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${value < 0 ? '-' : ''}${sym}${formatted}`;
}

/**
 * Shared currency formatter for bank balances and totals.
 * Uses Intl.NumberFormat for thousands separators and fixed 2-decimal precision.
 * Multi-char symbols (kr for DKK) get a separating space for readability.
 * Accepts string or number amounts (safe for API responses where balance is a string).
 *
 *   formatCurrency(1500, 'GBP')        => '£1,500.00'
 *   formatCurrency(372283.93, 'DKK')   => 'kr 372,283.93'
 *   formatCurrency(0, 'GBP')           => '£0.00'
 *   formatCurrency(-1500, 'EUR')       => '-€1,500.00'
 */
export function formatCurrency(amount, isoCode = 'GBP') {
  if (amount == null) return '';
  var num = Number(amount);
  if (isNaN(num)) return '';
  var sym = SYMBOLS[isoCode] || isoCode;
  var sep = sym.length > 1 ? ' ' : '';
  var abs = Math.abs(num);
  var formatted = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(abs);
  return (num < 0 ? '-' : '') + sym + sep + formatted;
}

/** Currency symbol for a given code */
export function currencySymbol(currency = 'GBP') {
  return SYMBOLS[currency] || currency;
}

export function formatPercent(value, decimals = 0) {
  if (value === null || value === undefined || isNaN(value)) return '';
  return `${value.toFixed(decimals)}%`;
}

/**
 * 12-colour palette for per-property charts.
 * Distinct hues, colour-blind sensitive (avoids red/green only differentiation).
 * Same palette used across UK and FR tabs so a property keeps its colour.
 */
export const PROPERTY_COLOR_PALETTE = [
  '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899',
  '#06B6D4', '#84CC16', '#F97316', '#6366F1', '#14B8A6',
  '#A855F7', '#EAB308',
];

/**
 * Shared region colours for UK/FR series across all portfolio charts.
 * Single source of truth: Gross, Net, Margin, ADR, Occupancy all reference
 * these tokens so a palette change propagates everywhere.
 */
export const REGION_COLORS = {
  UK: '#3B82F6',
  FR: '#10B981',
  COMBINED: '#000000',
};

/** Neutral grey for the prior-year reference line on revenue charts. */
export const PRIOR_YEAR_COLOR = '#9CA3AF';

/** Neutral grey for an explicit "Unassigned"/unresolved-entity stack segment. */
export const UNASSIGNED_COLOR = '#9CA3AF';

/**
 * Reconciliation-state colours, matching this page's existing Reconciled
 * badge (green) and Unreconciled stat-card text (amber) so a chart legend
 * reads consistently with the row table above it.
 */
export const RECONCILIATION_COLORS = {
  Reconciled: '#10B981',
  Unreconciled: '#F59E0B',
};

/**
 * Data-point labels (on-bar/on-line values) become unreadable clutter past
 * a certain density. 20 is generous headroom over the 12-month charts every
 * current consumer renders; this only engages if a chart is ever fed a
 * longer series.
 */
export function shouldSkipDataLabels(data) {
  return !!data && data.length > 20;
}
